import React, { useState } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Upload, Clock, Loader2, CheckCircle, AlertCircle, RefreshCw, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ref, getBlob } from "firebase/storage";
import { deleteField, doc, updateDoc } from "firebase/firestore";
import { db, storage } from "@/lib/firebase";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useUploadFileMutation } from "@/redux/api/documents.rtkq";
import { getTracePropagationFields } from "@/lib/sentry";

interface PendingTranscriptionRowProps {
  fileName: string;
  currentStep: "uploading" | "queued" | "processing" | "completed" | "error";
  progress: number;
  error?: string;
  id?: string;
  createdAt?: string;
  storagePath?: string;
  isWaitingForSync?: boolean;
  isReadOnly?: boolean;
  onDismiss?: () => void;
}

const relativeStoragePath = (storagePath: string, userId: string): string =>
  storagePath.startsWith(`${userId}/`) ? storagePath.slice(userId.length + 1) : storagePath;

const PendingTranscriptionRow: React.FC<PendingTranscriptionRowProps> = ({
  fileName,
  currentStep,
  error,
  id,
  storagePath,
  isWaitingForSync = false,
  isReadOnly = false,
  onDismiss,
}) => {
  const { user, getAccountId } = useAuthContext();
  const [uploadFile] = useUploadFileMutation();
  const [isResending, setIsResending] = useState(false);
  const canRetry = Boolean(id && storagePath && user?.uid);

  const getStepIcon = () => {
    switch (currentStep) {
      case "uploading":
        return <Upload className="w-4 h-4 text-blue-500" />;
      case "queued":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case "processing":
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStepText = () => {
    if (isWaitingForSync && currentStep === "completed") {
      return "ממתין לסנכרון";
    }

    switch (currentStep) {
      case "uploading":
        return "מעלה קובץ";
      case "queued":
        return "בתור לעיבוד";
      case "processing":
        return "מעבד תמלול";
      case "completed":
        return "הושלם";
      case "error":
        return "נכשל";
      default:
        return "ממתין";
    }
  };

  const getStepColor = () => {
    if (isWaitingForSync && currentStep === "completed") {
      return "bg-orange-50 text-orange-700";
    }

    switch (currentStep) {
      case "uploading":
        return "bg-blue-50 text-blue-700";
      case "queued":
        return "bg-yellow-50 text-yellow-700";
      case "processing":
        return "bg-blue-50 text-blue-700";
      case "completed":
        return "bg-green-50 text-green-700";
      case "error":
        return "bg-red-50 text-red-700";
      default:
        return "bg-gray-50 text-gray-700";
    }
  };

  const formatDate = () => {
    return new Date().toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleResend = async () => {
    if (!user?.uid || !id || !storagePath || isResending || isReadOnly) return;

    setIsResending(true);

    try {
      const pathParam = relativeStoragePath(storagePath, user.uid);
      const blob = await getBlob(ref(storage, `${user.uid}/${pathParam}`));
      const file = new File([blob], fileName, { type: blob.type || "application/octet-stream" });
      const now = new Date().toISOString();
      const transcriptionRef = doc(db, "users", user.uid, "transcriptions", id);

      await updateDoc(transcriptionRef, {
        "processing.status": "processing",
        "processing.error": deleteField(),
        "processing.errorCode": deleteField(),
        "processing.startedAt": now,
        updatedAt: now,
      });

      const { sentryTrace, sentryBaggage } = getTracePropagationFields();
      const officeId = getAccountId();
      const result = await uploadFile({
        file,
        path: pathParam,
        userId: user.uid,
        metadata: {
          customMetadata: {
            uid: user.uid,
            state: "processing",
            duration: "0",
            transcriptionId: id,
            uploadSource: "retry",
            ...(officeId ? { officeId } : {}),
            ...(sentryTrace ? { sentryTrace } : {}),
            ...(sentryBaggage ? { sentryBaggage } : {}),
          },
        },
      }).unwrap();

      if (result.error) {
        throw result.error;
      }

      toast.success("התמלול נשלח מחדש");
      onDismiss?.();
    } catch (resendError) {
      console.error("Error resending pending transcription:", resendError);
      toast.error("שגיאה בשליחה מחדש");
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    if (isReadOnly) return;
    onDismiss?.();
  };

  return (
    <TableRow
      className={cn(
        "hover:bg-muted/50 border-l-4 bg-gradient-to-r",
        currentStep === "completed" && "border-l-green-500 bg-gradient-to-r from-green-50/20 to-transparent",
        currentStep === "error" && "border-l-red-500 bg-gradient-to-r from-red-50/20 to-transparent",
        currentStep !== "completed" &&
          currentStep !== "error" &&
          "border-l-blue-500 bg-gradient-to-r from-blue-50/20 to-transparent animate-pulse"
      )}
    >
      {/* File Name Column */}
      <TableCell className="text-right align-top whitespace-normal">
        <div className="flex flex-col pr-4">
          <div className="text-chen-900 font-medium">{fileName}</div>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className={cn(getStepColor(), "hover:opacity-80 text-xs")}>
              {getStepIcon()}
              <span className="mr-1">{getStepText()}</span>
            </Badge>
            {currentStep === "processing" && (
              <Badge variant="secondary" className="bg-purple-50 text-purple-700 text-xs">
                <Loader2 className="w-3 h-3 ml-1 animate-spin" />
                Whisper AI
              </Badge>
            )}
          </div>
          {error && (
            <p className="max-w-full mt-1 text-xs text-destructive text-right leading-snug break-words" role="alert">
              {error}
            </p>
          )}
        </div>
      </TableCell>

      {/* Date Column */}
      <TableCell className="text-right">
        <div className="text-foreground">{formatDate()}</div>
      </TableCell>

      {/* Duration Column */}
      <TableCell className="text-right">
        <div className="text-muted-foreground">--:--:--</div>
      </TableCell>

      {/* Status Column */}
      <TableCell className="align-top text-right whitespace-normal">
        <div className="flex items-center gap-2">
          {getStepIcon()}
          <span className={currentStep === "error" ? "text-destructive font-medium" : "text-foreground"}>
            {getStepText()}
          </span>
        </div>
      </TableCell>

      {/* Actions Column */}
      <TableCell className="align-top text-right whitespace-nowrap">
        {currentStep === "completed" && !isWaitingForSync ? (
          <div className="text-sm text-green-600 animate-in fade-in duration-500">מוכן לשימוש - מועבר לטבלה</div>
        ) : currentStep === "completed" && isWaitingForSync ? (
          <div className="text-sm text-orange-600">ממתין לסנכרון עם השרת...</div>
        ) : currentStep === "error" ? (
          <TooltipProvider>
            <div className="flex items-center gap-2">
              {canRetry && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResend}
                      className="h-8 w-8 p-0 hover:bg-blue-50"
                      disabled={isResending || isReadOnly}
                      aria-label="שלח מחדש"
                    >
                      {isResending ? (
                        <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 text-blue-600" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>שלח מחדש</p>
                  </TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDismiss}
                    className="h-8 w-8 p-0 hover:bg-red-50"
                    disabled={isResending || isReadOnly}
                    aria-label="מחק תמלול"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>מחק תמלול</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        ) : (
          <div className="text-sm text-muted-foreground">מעבד...</div>
        )}
      </TableCell>
    </TableRow>
  );
};

export default PendingTranscriptionRow;
